import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Bot, ArrowRight, ArrowLeft, Loader2, Globe, ChevronDown, Check, Sparkles } from 'lucide-react';
import { useAgent } from '../../context/AgentContext';
import { Input, Textarea } from '../../components/Input';
import Button from '../../components/Button';
import { languages } from '../../data/dummyData';
import api from '../../services/api';

export default function StepName() {
  const { goNext, goPrev } = useOutletContext();
  const { currentAgent, createAgent, updateAgentName, isLoading, error, clearError } = useAgent();

  const [formData, setFormData] = useState({
    name: currentAgent?.name || '',
    businessType: currentAgent?.business_type || '',
    businessDescription: currentAgent?.description || '',
    language: currentAgent?.language || 'English',
  });
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [isFormatting, setIsFormatting] = useState(false);

  // Update form when agent data loads (for resume flow)
  useEffect(() => {
    if (currentAgent) {
      setFormData({
        name: currentAgent.name || '',
        businessType: currentAgent.business_type || '',
        businessDescription: currentAgent.description || '',
        language: currentAgent.language || 'English',
      });
    }
  }, [currentAgent]);

  const isValid = formData.name && formData.businessType && formData.businessDescription;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) clearError();
  };

  const handleFormatWithAI = async () => {
    if (!formData.businessDescription.trim() || isFormatting) return;

    setIsFormatting(true);
    try {
      const response = await api.formatWithAI(
        formData.businessDescription,
        'business_description',
        { business_type: formData.businessType }
      );
      if (response.formatted_text) {
        handleChange('businessDescription', response.formatted_text);
      }
    } catch (err) {
      console.error('Failed to format with AI:', err);
    } finally {
      setIsFormatting(false);
    }
  };

  const handleContinue = async () => {
    if (!isValid) return;

    try {
      const agentData = {
        name: formData.name,
        business_type: formData.businessType,
        description: formData.businessDescription,
        language: formData.language,
      };

      if (currentAgent?.id) {
        // Update existing agent
        await updateAgentName(agentData);
      } else {
        // Create new agent
        await createAgent(agentData);
      }

      goNext();
    } catch (err) {
      // Error is handled by context
      console.error('Failed to save agent:', err);
    }
  };

  return (
    <div className="p-8 max-w-2xl mx-auto animate-fade-in">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 rounded-xl bg-gradient-to-br from-primary to-accent">
            <Bot className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-text-primary">Name Your Agent</h1>
            <p className="text-text-secondary">Tell us about your business and bot</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          {error}
        </div>
      )}

      <div className="card p-6 space-y-6">
        <Input
          label="Bot Name"
          placeholder="e.g., Amani, Baraka, Zawadi, Neema"
          value={formData.name}
          onChange={(e) => handleChange('name', e.target.value)}
          helper="Choose a friendly name for your AI agent"
          disabled={isLoading}
        />

        <Input
          label="Business Type"
          placeholder="e.g., Restaurant, E-commerce Store, Salon, Real Estate Agency"
          value={formData.businessType}
          onChange={(e) => handleChange('businessType', e.target.value)}
          helper="What type of business do you run?"
          disabled={isLoading}
        />

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-sm font-medium text-text-primary">
              Business Description
            </label>
            <button
              type="button"
              onClick={handleFormatWithAI}
              disabled={isLoading || isFormatting || !formData.businessDescription.trim()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isFormatting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Sparkles className="w-3.5 h-3.5" />
              )}
              {isFormatting ? 'Formatting...' : 'Format with AI'}
            </button>
          </div>
          <textarea
            placeholder="Describe your business in simple words. What do you sell? What services do you offer? What makes you unique?"
            value={formData.businessDescription}
            onChange={(e) => handleChange('businessDescription', e.target.value)}
            rows={5}
            disabled={isLoading || isFormatting}
            className="input-field resize-none disabled:opacity-50 disabled:cursor-not-allowed"
          />
          <p className="mt-1.5 text-sm text-text-muted">
            Describe your business in simple words. This helps your AI understand what you do and respond appropriately to customers.
          </p>
        </div>

        {/* Language Dropdown */}
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Language
          </label>
          <div className="relative">
            <button
              type="button"
              onClick={() => !isLoading && setShowLanguageDropdown(!showLanguageDropdown)}
              disabled={isLoading}
              className="w-full input-field flex items-center justify-between text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center gap-3">
                <div className="p-1.5 rounded-lg bg-primary/10">
                  <Globe className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium text-text-primary">
                  {formData.language || 'Select Language'}
                </span>
              </div>
              <ChevronDown className={`w-5 h-5 text-text-muted transition-transform ${showLanguageDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showLanguageDropdown && (
              <div className="absolute z-20 mt-2 w-full bg-white border border-border rounded-xl shadow-lg max-h-64 overflow-y-auto animate-fade-in">
                {languages.map(lang => (
                  <button
                    key={lang}
                    type="button"
                    onClick={() => {
                      handleChange('language', lang);
                      setShowLanguageDropdown(false);
                    }}
                    className={`w-full p-3 flex items-center gap-3 hover:bg-background transition-colors text-left ${
                      formData.language === lang ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className="p-1.5 rounded-lg bg-primary/10 flex-shrink-0">
                      <Globe className="w-4 h-4 text-primary" />
                    </div>
                    <span className="font-medium text-text-primary flex-1">{lang}</span>
                    {formData.language === lang && (
                      <Check className="w-4 h-4 text-primary" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className="mt-1.5 text-sm text-text-muted">Select the primary language for your AI agent</p>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-8">
        <Button
          variant="ghost"
          icon={ArrowLeft}
          onClick={goPrev}
          disabled={isLoading}
        >
          Back
        </Button>
        <Button
          icon={isLoading ? Loader2 : ArrowRight}
          iconPosition="right"
          onClick={handleContinue}
          disabled={!isValid || isLoading}
          className={isLoading ? '[&_svg]:animate-spin' : ''}
        >
          {isLoading ? 'Saving...' : 'Continue'}
        </Button>
      </div>
    </div>
  );
}